import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { 
    Save, ArrowLeft, Plus, ChevronRight, Search, 
    Trash2, ShieldAlert, Zap, Info, Tag, Paperclip, History 
} from 'lucide-react';

// المودالات الموحدة من نظام الـ PO
import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal';
import StatusHistoryModal from './StatusHistoryModal';

const HazardDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id || id === 'new';
    
    const [activeTab, setActiveTab] = useState('Hazard');
    const [loading, setLoading] = useState(false);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [count, setCount] = useState(0);

    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const [data, setData] = useState({
        hazardnum: '', description: '', hazardtype: '', priority: 'MEDIUM',
        siteid: session.site_id || '', orgid: session.org_id || '',
        precautions: [],
        tagouts: [] // المخاطر بتربط بنقاط عزل برضه في Setup الأمان
    });

    const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', targetIdx: null });

    // 1. جلب البيانات
    useEffect(() => {
        if (!isNew) {
            axios.get(`/api/hazards/${id}`).then(res => {
                const d = res.data;
                d.precautions = d.precautions || [];
                d.tagouts = d.tagouts || [];
                setData(d);
            });
        }
    }, [id, isNew]);

    // 2. جلب عدد المرفقات
    const fetchCount = useCallback(async () => {
        if (!data.hazardnum || isNew) { setCount(0); return; }
        try {
            const res = await axios.get(`/api/attachments/count/HAZARD/${data.hazardnum}`);
            setCount(res.data.count || 0);
        } catch (err) { console.error(err); }
    }, [data.hazardnum, isNew]);

    useEffect(() => { fetchCount(); }, [fetchCount]);

    // --- الدوال الأساسية ---
    const handleSave = async () => {
        try {
            setLoading(true);
            await axios.post('/api/hazards/save', data);
            toast.success("Hazard Master Saved!");
            if (isNew) navigate(`/hazards/${data.hazardnum}`);
        } catch (err) { toast.error("Save failed"); } 
        finally { setLoading(false); }
    };

    const handleDelete = () => {
        Swal.fire({
            title: 'Are you sure?', text: "This will delete the hazard permanently!",
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`/api/hazards/${data.hazardnum}`);
                    toast.success("Hazard deleted!");
                    navigate('/hazards');
                } catch (err) { toast.error("Delete failed"); }
            }
        });
    };


    const handleLookupSelect = (row) => {
        const newData = { ...data };
        if (lookup.field === 'precautionnum') {
            newData.precautions[lookup.targetIdx].precautionnum = row.precautionnum;
            newData.precautions[lookup.targetIdx].description = row.description;
        } else if (lookup.field === 'tagoutnum') {
            newData.tagouts[lookup.targetIdx].tagoutnum = row.tagoutnum;
            newData.tagouts[lookup.targetIdx].description = row.description;
        } else {
            newData[lookup.field] = row.value || row.site_id || row.org_id;
        }
        setData(newData);
        setLookup({ ...lookup, isOpen: false });
    };
    // --- Global Handlers for Save/Delete (تقدر تشغلهم من أي مكان في التطبيق) ---  
    useEffect(() => {
        const triggerSave = () => handleSave();
        window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
        return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    }, [handleSave]);
    
    useEffect(() => {
        const triggerDelete = () => handleDelete();
        window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
        return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    }, [handleDelete]);
    return (
        <div className="flex flex-col h-screen bg-[#f3f4f6] text-[11px] font-sans uppercase">
            <UniversalLookup {...lookup} onClose={() => setLookup({ ...lookup, isOpen: false })} onSelect={handleLookupSelect} />
            {!isNew && <AttachmentsModal isOpen={isDocModalOpen} onClose={() => { setIsDocModalOpen(false); fetchCount(); }} ownertable="HAZARD" ownerid={data.hazardnum} />}
            {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="HAZARD" recordId={data.hazardnum} onClose={() => setShowHistoryModal(false)} />}

            {/* Header Bar - PO DNA */}
            <div className="bg-white p-2 border-b flex justify-between items-center shadow-sm z-20 font-bold tracking-tighter">
                <div className="flex gap-6 ml-4">
                    <button onClick={() => navigate('/hazards')} className="p-1 hover:bg-gray-100 rounded-full text-blue-900"><ArrowLeft size={18} /></button>
                    <HeaderField label="Hazard Num" value={data.hazardnum} isBold width="w-32" disabled={!isNew} onChange={v => setData({ ...data, hazardnum: v })} />
                    <HeaderField label="Description" value={data.description} onChange={v => setData({ ...data, description: v })} width="w-[300px]" />
                    <HeaderField label="Site" value={data.siteid} disabled width="w-24" />
                </div>
                <div className="flex gap-2 mr-4">
                    <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-blue-900 rounded-sm hover:bg-blue-600 hover:text-white transition-all">
                        <Paperclip size={18} />
                        {count > 0 && <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center border border-white">{count}</span>}
                    </button>
                    <button onClick={() => setShowHistoryModal(true)} className="p-2 text-blue-900 hover:bg-blue-50 rounded"><History size={16} /></button>
                    <button onClick={handleSave} disabled={loading} className="bg-[#3c70a4] text-white px-8 py-2 rounded font-black flex items-center gap-2 hover:bg-[#1a3353] shadow-lg">
                        <Save size={14} /> {loading ? 'SAVING...' : 'SAVE'}
                    </button>
                </div>
            </div>

            {/* Navigation Tabs - Blue Theme */}
            <div className="flex bg-[#1a3353] text-white">
                {[
                    { id: 'Hazard', label: 'Hazard Info', icon: <ShieldAlert size={14} /> },
                    { id: 'Precautions', label: 'Default Precautions', icon: <Tag size={14} /> },
                    { id: 'Tagouts', label: 'Linked Tag Outs', icon: <Zap size={14} /> }
                ].map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-10 py-3 flex items-center gap-2 font-black border-r border-white/5 ${activeTab === t.id ? 'bg-white text-[#1a3353]' : 'opacity-75 hover:opacity-100'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-[#f8f9fb]">
                {/* --- Tab 1: Hazard Info --- */}
                {activeTab === 'Hazard' && (
                    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
                        <SectionCard title="Hazard Definition">
                            <div className="grid grid-cols-2 gap-8 p-2">
                                <EditableField label="Hazard Number" value={data.hazardnum} disabled={!isNew} />
                                <EditableField label="Hazard Type" value={data.hazardtype} 
                                    onSearch={() => setLookup({ 
                                    isOpen: true, 
                                    title: 'Select Hazard Type', 
                                    type: 'DOMAIN', 
                                    domainId: 'HAZARDTYPE', // <--- ابعتها كـ Prop صريح هنا
                                    field: 'hazardtype', 
                                    filters: { domainid: 'HAZARDTYPE' } 
                                  })} 
                                />
                                <EditableField label="Organization" value={data.orgid} disabled />
                                <EditableField label="Priority" value={data.priority} onChange={v => setData({...data, priority: v})} />
                                <div className="col-span-2">
                                    <EditableField label="Full Description" value={data.description} onChange={v => setData({ ...data, description: v })} />
                                </div>
                            </div>
                        </SectionCard>
                    </div>
                )}

                {/* --- Tab 2: Default Precautions --- */}
                {activeTab === 'Precautions' && (
                    <div className="max-w-6xl mx-auto animate-in slide-in-from-bottom duration-500">
                        <SectionCard title="Predefined Precautions for this Hazard">
                            <div className="bg-white border rounded overflow-hidden">
                                <table className="w-full text-left">
                                    <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold uppercase text-[9px]">
                                        <tr>
                                            <th className="p-2 border-r w-40">Precaution Num</th>
                                            <th className="p-2 border-r">Instruction / Description</th>
                                            <th className="p-2 text-center w-12">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.precautions.map((p, i) => (
                                            <tr key={i} className="border-b hover:bg-blue-50">
                                                <td className="p-2 border-r font-black text-blue-900 group">
                                                    <div className="flex justify-between items-center px-1">
                                                        {p.precautionnum || '---'}
                                                        <Search size={12} className="text-blue-400 cursor-pointer" 
                                                            onClick={() => setLookup({ isOpen: true, title: 'Select Precaution', type: 'precautions', field: 'precautionnum', targetIdx: i })} />
                                                    </div>
                                                </td>
                                                <td className="p-2 border-r font-semibold text-gray-600 uppercase">{p.description}</td>
                                                <td className="p-2 text-center">
                                                    <Trash2 size={12} className="text-red-400 hover:text-red-700 cursor-pointer mx-auto" 
                                                        onClick={() => {
                                                            const np = data.precautions.filter((_, idx) => idx !== i);
                                                            setData({...data, precautions: np});
                                                        }} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button onClick={() => setData({...data, precautions: [...data.precautions, {precautionnum:'', description:''}]})} 
                                    className="m-2 bg-slate-100 text-slate-600 hover:bg-blue-800 hover:text-white px-4 py-1 rounded-sm font-black flex items-center gap-2 transition-all">
                                    <Plus size={12} /> ADD PRECAUTION LINE
                                </button>
                            </div>
                        </SectionCard>
                    </div>
                )}

                {/* --- Tab 3: Linked Tag Outs --- */}
                {activeTab === 'Tagouts' && (
                    <div className="max-w-6xl mx-auto animate-in slide-in-from-bottom duration-500">
                        <SectionCard title="Required Isolation Points (LOTO Master)">
                            <div className="bg-white border rounded overflow-hidden">
                                <table className="w-full text-left">
                                    <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold uppercase text-[9px]">
                                        <tr>
                                            <th className="p-2 border-r w-40">Tag Out Num</th>
                                            <th className="p-2 border-r">Isolation Description</th>
                                            <th className="p-2 text-center w-12">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.tagouts.map((t, i) => (
                                            <tr key={i} className="border-b hover:bg-blue-50">
                                                <td className="p-2 border-r font-black text-blue-900 px-3">
                                                    <div className="flex justify-between items-center">
                                                        {t.tagoutnum || '---'}
                                                        <Search size={12} className="text-blue-400 cursor-pointer" 
                                                            onClick={() => setLookup({ isOpen: true, title: 'Select TagOut', type: 'tagouts', field: 'tagoutnum', targetIdx: i })} />
                                                    </div>
                                                </td>
                                                <td className="p-2 border-r font-semibold text-gray-500">{t.description}</td>
                                                <td className="p-2 text-center">
                                                    <Trash2 size={12} className="text-red-400 cursor-pointer mx-auto" 
                                                        onClick={() => {
                                                            const nt = data.tagouts.filter((_, idx) => idx !== i);
                                                            setData({...data, tagouts: nt});
                                                        }} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button onClick={() => setData({...data, tagouts: [...data.tagouts, {tagoutnum:'', description:''}]})} 
                                    className="m-2 bg-slate-100 text-slate-600 hover:bg-blue-800 hover:text-white px-4 py-1 rounded-sm font-black flex items-center gap-2 transition-all">
                                    <Plus size={12} /> ADD TAG OUT LINE
                                </button>
                            </div>
                        </SectionCard>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Reusable Components (نفس الستايل بتاعك بالظبط) ---

const HeaderField = ({ label, value, onChange, width, isBold, disabled }) => (
    <div className="flex flex-col border-r px-4 last:border-0">
        <label className="text-[8px] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
        <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[12px]' : 'text-blue-700'} bg-transparent focus:border-blue-500`}
            value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
    </div>
);

const SectionCard = ({ title, children }) => (
    <div className="bg-white border rounded shadow-sm overflow-hidden transition-all hover:shadow-md">
        <div className="p-2 bg-slate-50 border-b font-black text-slate-500 uppercase text-[9px] tracking-widest flex items-center gap-2">
            <div className="w-1.5 h-3 bg-blue-800 rounded-full"></div> {title}
        </div>
        <div className="p-5">{children}</div>
    </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled }) => (
    <div className="flex flex-col gap-1 border-b border-gray-200 group focus-within:border-blue-500 transition-all py-1">
        <label className="text-gray-400 font-black text-[8px] uppercase tracking-widest italic">{label}</label>
        <div className="flex items-center justify-between pb-0.5">
            <input type={type} disabled={disabled} className="outline-none font-black text-blue-900 bg-transparent w-full text-[11px]"
                value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
            {onSearch && <Search size={12} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors" onClick={onSearch} />}
        </div>
    </div>
);

export default HazardDetails;